import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import pathToRegexp from 'path-to-regexp';
import { Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { compose, pure } from 'utils/react';
import classes from './CertSettings.scss';

class Certificate extends React.Component {
  static propTypes = {
    domains: PropTypes.arrayOf(PropTypes.string).isRequired,
    validTo: PropTypes.string.isRequired,
    fingerprint: PropTypes.string.isRequired,
    isSynced: PropTypes.bool.isRequired,
    onDelete: PropTypes.func.isRequired,
    siteHostnames: PropTypes.arrayOf(PropTypes.string).isRequired,
  };

  handleDelete = () => {
    const { fingerprint, onDelete } = this.props;
    onDelete(fingerprint);
  };

  hasMatchingHostname() {
    const { domains, siteHostnames } = this.props;

    return domains.some((domain) => {
      const domainExpression = pathToRegexp(domain);

      return siteHostnames.some(hostname => domainExpression.test(hostname));
    });
  }

  renderUnsyncedWarning() {
    if (this.props.isSynced) {
      return false;
    }

    return (
      <Alert variant="warning" className={classes.unsyncedWarning}>
        <b>Uh oh!</b> The certificate isn't synchronized with the web servers. Something went
        very wrong.
      </Alert>
    );
  }

  renderNonmatchingWarning() {
    const { fingerprint } = this.props;
    const tooltip = (
      <Tooltip id={`hostname-warning-${fingerprint.replace(/[^\w\d]+/g, '-')}`}>
        You have no domains that match the domains for this certificate. Considering adding the
        domain to your domains in the Domain tab.
      </Tooltip>
    );

    return (
      <OverlayTrigger placement="top" overlay={tooltip}>
        <i
          className={[
            'fa',
            'fa-exclamation-triangle',
            classes.hostnameWarning,
          ].join(' ')}
        />
      </OverlayTrigger>
    );
  }

  render() {
    const {
      domains, validTo, fingerprint, isSynced,
    } = this.props;

    if (!domains) return null;

    const classNames = [classes.certificate];
    const hasMatchingHostname = this.hasMatchingHostname();
    const validTime = moment(parseInt(validTo, 10));
    const isValid = moment(parseInt(validTo, 10)).startOf('day').diff(moment()) >= 0;

    if (!isValid) {
      classNames.push(classes.invalid);
    } else if (!isSynced || !hasMatchingHostname) {
      classNames.push(classes.hasIssue);
    } else {
      classNames.push(classes.valid);
    }

    return (
      <div className={classNames.join(' ')}>
        <div>
          <span className={classes.label}>Domains</span>
          {' '}
          <ul className={classes.domains}>
            {domains.map((domain, domainIndex) => (
              <li key={domainIndex}>
                <code>{domain}</code>
              </li>
            ))}
          </ul>
          {' '}
          {!hasMatchingHostname ? this.renderNonmatchingWarning() : false}
        </div>
        <div>
          <span className={classes.label}>Expires</span>
          {' '}
          {validTime.format('MMMM D, YYYY')}
        </div>
        <div>
          <span className={classes.label}>Fingerprint</span>
          {' '}
          <code>{fingerprint}</code>
        </div>
        {this.renderUnsyncedWarning()}
        <span
          className={`fa fa-trash ${classes.deleteIcon}`}
          onClick={this.handleDelete}
        />
      </div>
    );
  }
}

export default compose(
  pure(),
)(Certificate);
